import { getCurrencyFormat, moneyConfig, getSolidusSetting } from "./config"
import { TMoney } from "./types"
import { getMaximumFractionDigits } from "./utils"

/*
 * System formats (Unix/Servers) generally use underscore format
 * but, on web, we use hyphen format.
 */

function getWebLocaleForSystemFormat(systemFormat: string): string {
	switch (systemFormat) {
		case "en_US":
			return "en-US"
		case "de_DE":
			return "de-DE"
		case "en_IN":
		default:
			return "en-IN"
	}
}

export function formatMoneyByIntl(
	money: TMoney,
	config: {
		keepTrailingZero?: boolean
		showCurrency?: boolean
		roundOff?: boolean
		showSolidus?: boolean
	} = {
		keepTrailingZero: false,
		showCurrency: false,
		roundOff: false,
		showSolidus: undefined,
	}
): string {
	// TODO: Remove the fallback and ensure that a currency is passed
	const currency = money.currency || moneyConfig.defaultCurrency
	// Use format from config if provided, otherwise from money.currency, otherwise default
	const format = getCurrencyFormat(currency) || "en_IN"

	// Get the maximumFractionDigits for the currency
	const tempFormatter = new Intl.NumberFormat(
		getWebLocaleForSystemFormat(format),
		{
			style: "currency",
			currency,
		}
	)
	const maximumFractionDigits = getMaximumFractionDigits(tempFormatter)

	// Convert integer amount to decimal (e.g., 120090 -> 1200.90)
	let amount = money.amount / Math.pow(10, maximumFractionDigits)

	// Apply rounding if requested
	if (config?.roundOff) {
		amount = Math.round(amount)
	}

	// Check if the decimal amount is a whole number (e.g., 1200.00 -> true, 1200.90 -> false)
	const isWholeNumber = Number.isInteger(amount)

	// Create formatter with appropriate minimumFractionDigits
	const formatter = new Intl.NumberFormat(getWebLocaleForSystemFormat(format), {
		style: "currency",
		currency,
		currencyDisplay: "code",
		minimumFractionDigits: config?.keepTrailingZero
			? maximumFractionDigits
			: isWholeNumber
				? 0
				: undefined,
		maximumFractionDigits,
	})
	let formattedAmount = formatter.format(amount)
	if (!config?.showCurrency) {
		formattedAmount = formattedAmount.replace(currency, "").trim()
	}

	// Add solidus if configured (either from config param or global setting)
	const shouldShowSolidus =
		config?.showSolidus !== undefined
			? config.showSolidus && getSolidusSetting()
			: false

	// Show solidus only if all these conditions hold:
	// 1. shouldShowSolidus is true
	// 2. The formatted amount is displayed as an integer (no decimal part shown, i.e., either fully rounded or max fraction digits are all zeroed out).
	// 3. The current displayed amount is not using fractions (not like 1.25, or 1.00).
	// 4. We never hard code currency exceptions.

	if (shouldShowSolidus) {
		// Detect the decimal separator used by this formatter (locale-dependent)
		// Format 1.1 and remove the formatted 1 to get just the decimal part
		const decimalSeparator = formatter
			.format(1.1)
			.replace(formatter.format(1), "")
			.replace(/[^.,٫،·ʼ'٫]/g, "")

		// Show solidus only if:
		// - The formatted string has no decimal separator (integer display)
		// - The formatter supports fraction digits (to avoid showing for currencies like JPY)
		const hasDecimalSeparator =
			decimalSeparator && formattedAmount.includes(decimalSeparator)
		const showSolidus = !hasDecimalSeparator && maximumFractionDigits > 0

		if (showSolidus) {
			formattedAmount += " /-"
		}
	}

	return formattedAmount
}

export function formatMoneyByDecimal(
	money: TMoney,
	config: { keepTrailingZero?: boolean } = { keepTrailingZero: false }
): string {
	// TODO: Remove the fallback and ensure that a currency is passed
	const currency = money.currency || moneyConfig.defaultCurrency
	const format = getCurrencyFormat(currency) || "en_IN"
	const formatter = new Intl.NumberFormat(getWebLocaleForSystemFormat(format), {
		style: "currency",
		currency: currency,
	})
	const maximumFractionDigits = getMaximumFractionDigits(formatter)
	const amount = money.amount / Math.pow(10, maximumFractionDigits)
	if (!config.keepTrailingZero && Math.ceil(amount) == Math.floor(amount)) {
		return Number(amount).toFixed(0)
	}
	return Number(amount).toFixed(maximumFractionDigits)
}
